import { useMemo } from 'react';
import { CmsOffer } from '../../../../../../../types/cmsProduct';
import { useCmsClientContext } from '../../../../../../../utils/cmsPageContext';
import { LoanSearchParams } from '../../../../../../../utils/searchParams/loanSearchParams';
import { getClientTranslations } from '../../../../../../../utils/translations/getClientTranslations';
import { tr } from '../../../../../../../utils/translations/tr';
import { TRANSLATIONS } from '../../../i18n';
import { ProductDetailsSectionOffersPlTableProps } from '../ProductDetailsSectionOffersPlTable';
import { coversAmount } from './utils/coversAmount';
import { getCell } from './utils/getCell';
import { getLoanBands } from './utils/getLoanBands';
import {
  getTenureColumns,
  UNDER_12_COLUMN_KEY,
} from './utils/getTenureColumns';

type UseProductDetailsSectionOffersPlTableParams = {
  offers: CmsOffer[];
  loanSearchParams?: LoanSearchParams;
  onLoanSearchParamsChange?: (params: LoanSearchParams) => void;
};

// Builds the matrix props from the offers and the page's loan selection.
// Returns null when no gift carries a loan range (the matrix is hidden).
export const useProductDetailsSectionOffersPlTable = ({
  offers,
  loanSearchParams,
  onLoanSearchParamsChange,
}: UseProductDetailsSectionOffersPlTableParams): Omit<
  ProductDetailsSectionOffersPlTableProps,
  'onOpenDetails'
> | null => {
  const context = useCmsClientContext();
  const { currencyDisplay } = context;
  const text = getClientTranslations(TRANSLATIONS, context);

  const { rowHeaderLabel, bands, columns, cells } = useMemo(() => {
    const criteria = offers
      .flatMap((offer) => offer.list_of_gifts ?? [])
      .flatMap((gift) => gift.criteria_list ?? []);
    const loanRanges = criteria.filter(
      (criterion) => criterion.name?.name === 'ELIGIBLE_LOAN_RANGE',
    );
    const tenureRanges = criteria.filter(
      (criterion) => criterion.name?.name === 'ELIGIBLE_TENURE_RANGE',
    );
    const loanBands = getLoanBands(loanRanges, currencyDisplay, text);
    const tenureColumns = getTenureColumns(tenureRanges, text);

    return {
      rowHeaderLabel:
        loanRanges.find((range) => range.label)?.label ||
        tr('loan_amount', text),
      bands: loanBands,
      columns: tenureColumns,
      cells: Object.fromEntries(
        loanBands.map((band) => [
          band.key,
          Object.fromEntries(
            tenureColumns.map((column) => [
              column.key,
              getCell(offers, band.start, column.tenure, currencyDisplay),
            ]),
          ),
        ]),
      ),
    };
  }, [offers, currencyDisplay, text]);

  if (bands.length === 0) return null;

  const amount = Number(loanSearchParams?.amount);
  const tenure = Number(loanSearchParams?.tenure);
  const selectedBand = bands.find((band) => coversAmount(band, amount));
  const selectedColumn = columns.find((column) =>
    column.key === UNDER_12_COLUMN_KEY ? tenure < 12 : column.tenure === tenure,
  );

  // One shared selection with the calculator (CMS-2023 Q5): a cell keeps the
  // current amount when it is inside the band, otherwise takes the band Min;
  // the tenure comes from the column. A column heading keeps the amount.
  const onCellSelect = (rowKey: string, columnKey: string) => {
    const band = bands.find((item) => item.key === rowKey);
    const column = columns.find((item) => item.key === columnKey);
    if (!band || !column) return;
    onLoanSearchParamsChange?.({
      amount: String(coversAmount(band, amount) ? amount : band.start),
      tenure: String(column.tenure),
    });
  };

  const onColumnSelect = (columnKey: string) => {
    const column = columns.find((item) => item.key === columnKey);
    if (!column || !loanSearchParams) return;
    onLoanSearchParamsChange?.({
      amount: loanSearchParams.amount,
      tenure: String(column.tenure),
    });
  };

  return {
    rowHeaderLabel,
    rows: bands,
    columns,
    cells,
    selected:
      selectedBand && selectedColumn
        ? { rowKey: selectedBand.key, columnKey: selectedColumn.key }
        : undefined,
    onCellSelect,
    onColumnSelect,
  };
};
